import { useCallback, useEffect, useState } from 'react';
import { useMutation, useReactiveVar } from '@apollo/client';
import { toast } from 'react-toastify';
import useCollectedFilters from '../../../trimLevels/hooks/useCollectedFilters';
import deal from '../../graphql/mutations/createDeal';
import useGetPostCode from '../../../gqlCommon/hooks/useGetPostCode';
import { financeFormInputVar } from '../../../gqlCommon/graphql/cache';
import urlMinify from '../../../common/utils/urlMinify';
import useErrorHandler from '../../../gqlCommon/hooks/useErrorHandler';
import { JourneyType, SelectedCarForDeal } from '../../../__generated__/globalTypes';
import {
  CreateDealFromCarId,
  CreateDealFromCarIdVariables,
} from '../../graphql/mutations/__generated__/CreateDealFromCarId';
import usePostcodeErrorHandler from '../../../gqlCommon/hooks/usePostcodeErrorHandler';
import insuranceBlockService from '../../service/insuranceBlockService';
import availableFeaturesService from '../../service/availableFeaturesService';
import useWidgetContext from '../../../common/react/useWidgetContext';
import usePlatformConfig from '../../../common/react/usePlatformConfig';
import { tokenService } from '../../../../../common/service/tokenService';
import useCustomizeCarContext from '../useCustomizeCarContext';
import useStockLockContext from '../../context/stockLock/useStockLockContext';
import stockLockService from '../../service/stockLockService';
import { useSelectedFeatures } from '../features/useSelectedFeatures';
import adFixusService from '../../service/adFixusService';
import { utmSessionName } from '../../../../../common/constants/utm';
import useCustomerStorage from '../../../common/hooks/useCustomerStorage';

export const useCreateDeal = () => {
  const {
    platformConfig: { myAccountDxpIntegration },
  } = usePlatformConfig();
  const {
    configuration: { dimensions },
  } = useWidgetContext();
  const { brand, country } = dimensions;
  const { filters } = useCollectedFilters();
  const postCodeValue = useGetPostCode();
  const { setShowStockLockModal } = useStockLockContext();
  const { customerStorage } = useCustomerStorage();
  const [errorState, setErrorState] = useState(null);

  const financeFormInput = useReactiveVar(financeFormInputVar);

  const { selectedCar } = useCustomizeCarContext();
  const isInStock = !!selectedCar?.stockInfo;
  const carId = selectedCar?.car?.code;

  const authToken = tokenService.getCurrentToken(dimensions);
  const adFixusId = adFixusService.getAdFixusId();

  const [createDeal, { error, loading }] = useMutation<CreateDealFromCarId, CreateDealFromCarIdVariables>(
    deal.CREATE_DEAL,
    {
      context: {
        headers: {
          ...(myAccountDxpIntegration && !!authToken && { Authorization: authToken || '' }),
          ...(!!adFixusId && { 'X-ADFIXUS-ID': adFixusId }),
        },
      },
    },
  );
  const { selectedFeatures } = useSelectedFeatures();
  const utmDetailsInput = JSON.parse(sessionStorage.getItem(`${utmSessionName}_${brand}_${country}`));

  const createNewDeal = useCallback(
    async (journeyType: JourneyType) => {
      const minifiedConfigurationUrl = await urlMinify.shorten(window.location.toString());
      const selectedCar: SelectedCarForDeal = {
        filters,
        selectedFeatureIds: { selected: availableFeaturesService.getSelectedFeaturesCode(selectedFeatures) },
        configurationSourceUrl: minifiedConfigurationUrl,
        isStock: isInStock,
        ...(!!carId && { id: carId }),
      };
      const insuranceFormInput = insuranceBlockService.getFormInput(dimensions);
      const { dealerId } = customerStorage.getCustomerData() || {};

      return await createDeal({
        variables: {
          selectedCar,
          journeyType,
          financeFormInput: financeFormInput,
          postCode: postCodeValue,
          insuranceFormInput,
          dealerId,
          ...(!!utmDetailsInput && { utmDetailsInput }),
        },
      });
    },
    [carId, filters, dimensions, isInStock, selectedFeatures, financeFormInput, postCodeValue],
  );

  useEffect(() => {
    if (error || error?.message) {
      const hasStockLock = stockLockService.hasStockLock(error);
      if (hasStockLock) {
        setShowStockLockModal(true);
        return;
      }
      toast.error(error?.message ?? error);
    }
  }, [error]);

  usePostcodeErrorHandler(error, loading, setErrorState);
  useErrorHandler(errorState);

  return {
    createNewDeal,
    loading,
  };
};
